import { useWhiteLabel } from '@allesauto/core/whiteLabel';
import { PaymentType, SchemaTypes } from '@allesauto/graphql';
import translate from '@allesauto/i18n';
import { pushAnalyticsEvent } from '@allesauto/utils/analytics';
import { consts, FinanceTypeEn, ViewModes } from '@allesauto/utils/consts';
import { FuelType, VehicleCondition } from '@allesauto/utils/consts.ts';
import {
  createLinkForRouting,
  getFinanceCalculationSummary,
  getFinancePerCarDetails,
  isRueschampWhiteLabel,
  verifyVehicleDate,
} from '@allesauto/utils/funcs.ts';
import { useIntersectionViewport } from '@allesauto/utils/hooks';
import { Routes_DE as Routes } from '@allesauto/utils/routes';
import {
  handleTrackingEvent,
  SELECT_ITEM_EVENT,
  useProductTracking,
} from '@allesauto/utils/trackingUtils';
import { ArrowRight32 } from '@carbon/icons-react';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import React, { FC, useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import './ProductCard.css';

import CircularLoader, { CircularLoaderType } from '../circularLoader';
import Label from '../label';
import LikeButton from '../likeButton/';
import Price from '../price';
import { useConsumption } from '../useConsumption';
import { useEmission } from '../useEmission';
import VehicleImage from '../vehicleImage';

export interface ProductCardProps {
  vehicle: SchemaTypes.Vehicle;
  view: ViewModes;
  selectedFinance: SchemaTypes.PriceRange;
  isLastItem?: boolean;
  openOverlay?: (
    summaryText: string,
    legalText: string,
    financeType: SchemaTypes.PaymentType,
    vehicleName: string,
    vehicleId: string,
    vehicleModel: string,
    vehicleMark: string
  ) => void;
  openLoadingOverlay?: (isOpen: boolean) => void;
  numberOfItemsOnRow: number;
  additionalTracking: {
    item_list_name: string;
    item_list_id: string;
  };
  setPersistVehicleState?: (persistentState: any) => any;
  isWhiteLabel?: boolean;
  hasFavorites: boolean;
}

const vehiclePropExists = (prop: any, ensureIsNumber = true): boolean =>
  typeof prop !== 'undefined' &&
  prop !== null &&
  (ensureIsNumber ? !isNaN(prop) : true) &&
  (typeof prop === 'string' ? !!prop.length : true);

const ProductCard: FC<ProductCardProps> = ({
  vehicle,
  view,
  selectedFinance,
  isLastItem,
  openOverlay,
  openLoadingOverlay,
  additionalTracking: { item_list_id, item_list_name },
  numberOfItemsOnRow,
  setPersistVehicleState,
  isWhiteLabel = false,
  hasFavorites = true,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const intl = useIntl();
  const refTextlink = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const whiteLabel = useWhiteLabel();
  const emissionValue = useEmission(vehicle.wltEmission!, vehicle.emission!);
  const { consumptionValue } = useConsumption({
    fuelType: vehicle.fuelType,
    electricConsumption: vehicle.electricConsumption,
    consumption: vehicle.consumption,
    wltEmission: vehicle.wltEmission,
    wltConsumption: vehicle.wltConsumption,
    wltConsumptionElectric: vehicle.wltConsumptionElectric,
    hasConfiguratorDealer: false,
  });

  const triggerTracking = useProductTracking(
    vehicle,
    item_list_id,
    item_list_name,
    numberOfItemsOnRow
  );

  const productRef = useIntersectionViewport(
    (id) => triggerTracking(id),
    vehicle.id,
    { threshold: consts.INTERSECTION_OBSERVER.THRESHOLD }
  );

  const { monthlyPrice, summaryText, paymentSelected, legalText } =
    getFinancePerCarDetails(
      vehicle.financingDetails,
      vehicle.financeCalculatorOptions,
      selectedFinance.key as SchemaTypes.PaymentType
    );

  const financeTypeText =
    (paymentSelected &&
      translate(intl, `ui.FINANCE_DETAILS.${paymentSelected}`)) ||
    '';

  const className = {
    priceClass: clsx(
      'flex',
      isRueschampWhiteLabel(whiteLabel?.merchantOrigin) &&
        'sm:px-[20px] sm:py-[20px]',
      view === ViewModes.GRID
        ? '!justify-between mt-30px'
        : 'flex-col pr-40px pb-40px pt-40px h-full justify-between absolute right-0 top-0 w-[200px]'
    ),
    labelAndLikeClass: clsx(
      'mb-5px ',
      isRueschampWhiteLabel(whiteLabel?.merchantOrigin) &&
        'sm:px-[20px] sm:py-[20px]',
      view === ViewModes.GRID ? 'flex justify-between' : 'flex'
    ),
    ulClass:
      view === ViewModes.GRID
        ? 'table w-[100%] mt-30px'
        : 'mt-[43px] columns-2 xl:max-w-[70%]',
    containerClass: clsx(
      'aa-product-card bg-white cursor-pointer group no-underline relative ',
      isRueschampWhiteLabel(whiteLabel?.merchantOrigin)
        ? 'sm:p-[10px]'
        : 'sm:p-[40px]',
      !isRueschampWhiteLabel(whiteLabel?.merchantOrigin) &&
        'sm:p-[45px] md:p-[33px]',
      view === ViewModes.LIST ? 'grid tile-href-list' : 'tile-href-grid',
      view === ViewModes.LIST && !isLastItem && 'mb-20px'
    ),
    liLabelClass: clsx(
      'table-cell text-grey50 text-p2',
      view === ViewModes.GRID
        ? 'w-1/2 sm:text-[12px] md:text-[14px]'
        : 'text-[16px]'
    ),
    liValueClass: clsx(
      'table-cell text-black text-p2',
      view === ViewModes.GRID
        ? 'pl-5px sm:text-[12px] md:text-[14px]'
        : 'pl-10px text-[16px]'
    ),
    loadingOverlay:
      'w-full h-full absolute top-0 left-0 bg-opacity-90 bg-white flex items-center justify-center',
    liWrapperClass: clsx('items-center pb-5px table-row'),
    carBrandAndModel:
      view === ViewModes.LIST
        ? 'aa-product-card__title--list font-headline text-purple80 monochrome:text-black'
        : 'aa-product-card__title font-headline text-purple80 monochrome:text-black',
    arrowClass: clsx(
      'h-40px opacity-0 w-40px group-hover:opacity-100',
      'text-purple80 monochrome:text-black',
      view === ViewModes.GRID ? 'self-center' : 'self-end'
    ),
    titleLink: clsx(
      'no-underline',
      'before:absolute before:h-full before:left-0 before:top-0 before:w-full before:z-[5]',
      view === ViewModes.LIST && 'flex'
    ),
  };

  const dispatch = useDispatch();

  //sanitized URL for missing vehicle data from BE
  //see ticket https://jira.mediawave.de/browse/AAA-1448 for more details
  const pdpLink = createLinkForRouting(
    FinanceTypeEn.EMPTY,
    Routes.PDP_VEHICLES,
    {
      markName: vehicle.makeName && `/${vehicle.makeName}`,
      modelName: vehicle.modelName && `/${vehicle.modelName}`,
      id: vehicle.id && `/${vehicle.id}`,
    }
  );

  const handleFinancingDetailsClick = useCallback(
    async (e: React.MouseEvent) => {
      setPersistVehicleState &&
        dispatch(setPersistVehicleState({ persistentState: false }));
      e.stopPropagation();
      e.preventDefault();
      const summary = await getFinanceCalculationSummary(
        vehicle.id,
        paymentSelected!
      );
      if (openOverlay && paymentSelected) {
        openOverlay(
          summary ? summary : summaryText || '',
          legalText || '',
          paymentSelected,
          `${vehicle.makeName} ${vehicle.modelName}`,
          vehicle.id,
          vehicle.modelName || '',
          vehicle.makeName || ''
        );
      }
    },
    [
      setPersistVehicleState,
      dispatch,
      openOverlay,
      paymentSelected,
      summaryText,
      legalText,
      vehicle.makeName,
      vehicle.modelName,
      vehicle.id,
    ]
  );

  const handleCardClick = useCallback(
    async (e: React.MouseEvent) => {
      setPersistVehicleState &&
        dispatch(setPersistVehicleState({ persistentState: false }));
      e.stopPropagation();
      e.preventDefault();
      setLoading(true);
      openLoadingOverlay?.(true);
      pushAnalyticsEvent({
        event: 'link_click',
        elementUrl: `${window.location.origin}${pdpLink}`,
      });
      handleTrackingEvent(
        SELECT_ITEM_EVENT,
        item_list_id,
        item_list_name,
        vehicle
      );
      await router.push(pdpLink);
      setLoading(false);
      openLoadingOverlay?.(false);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [item_list_id, item_list_name, openLoadingOverlay, pdpLink, router, vehicle]
  );

  const imageFilename = vehicle.imageUrl;

  const onFavoritesClick = () => {
    // console.log('implement add to favorites')
  };

  const maxLength = 32;
  const connectedTitleMakeAndModel = `${vehicle.makeName?.replace(
    /_/g,
    ''
  )} ${vehicle.modelName?.replace(/_/g, ' ')}`;
  const shortenedTitle =
    connectedTitleMakeAndModel.length > maxLength
      ? connectedTitleMakeAndModel.substring(0, maxLength - 3) + '...'
      : connectedTitleMakeAndModel;

  return (
    <article ref={productRef} className={className.containerClass}>
      <div className="mb-[24px] wrapper-picture">
        <div className={className.labelAndLikeClass}>
          {vehicle.vehicleCondition && (
            <Label text={vehicle.vehicleCondition} />
          )}
          {hasFavorites && !isWhiteLabel && (
            <LikeButton onClick={onFavoritesClick} darkBg={true} />
          )}
        </div>
        <VehicleImage
          alt={vehicle.modelName || undefined}
          filename={imageFilename ?? undefined}
          fallbackImage="pcp_pdp"
          isWhiteLabel={isWhiteLabel}
          isLazyLoaded
        />
      </div>
      <div
        className={clsx(
          'mt-0',
          isRueschampWhiteLabel(whiteLabel?.merchantOrigin) && 'sm:px-[20px]'
        )}
      >
        <div className="w-full">
          <div className={view === ViewModes.GRID ? '' : 'flex flex-row'}>
            <a
              className={className.titleLink}
              href={pdpLink}
              onClick={handleCardClick}
            >
              <span className={className.carBrandAndModel}>
                {shortenedTitle}
              </span>
            </a>
          </div>
        </div>
        <p className="break-words min-h-[1.4rem] pt-5px text-[16px] text-black sm:text-[14px]">
          {vehicle.trim}
        </p>
        <ul className={className.ulClass}>
          {vehiclePropExists(vehicle.mileage) && (
            <li className={className.liWrapperClass}>
              <p className={className.liLabelClass}>
                {translate(intl, 'ui.PCP.PRODUCT_CARD.MILEAGE')}
              </p>
              <p className={className.liValueClass}>
                {translate(intl, 'ui.UNITS.MILEAGE', {
                  value: intl.formatNumber(vehicle.mileage as number),
                })}
              </p>
            </li>
          )}
          {
            <li className={className.liWrapperClass}>
              <p className={className.liLabelClass}>
                {translate(intl, 'ui.PCP.PRODUCT_CARD.FIRST_REGISTRATION')}
              </p>
              <p className={className.liValueClass}>
                {verifyVehicleDate(vehicle.registeredIn)}
              </p>
            </li>
          }
          {vehiclePropExists(vehicle.powerHp) &&
            vehiclePropExists(vehicle.powerKw) && (
              <li className={className.liWrapperClass}>
                <p className={className.liLabelClass}>
                  {translate(intl, 'ui.PCP.PRODUCT_CARD.PERFORMANCE')}
                </p>
                <p className={className.liValueClass}>
                  {translate(intl, 'ui.UNITS.POWER_KW_HP', {
                    valueKW: intl.formatNumber(vehicle.powerKw as number),
                    valueHP: intl.formatNumber(vehicle.powerHp as number),
                  })}
                </p>
              </li>
            )}
          {vehiclePropExists(vehicle.fuelType, false) && (
            <li className={className.liWrapperClass}>
              <p className={className.liLabelClass}>
                {translate(intl, 'ui.PCP.PRODUCT_CARD.FUELTYPE')}
              </p>
              <p className={className.liValueClass}>{vehicle.fuelType}</p>
            </li>
          )}
          <li className={className.liWrapperClass}>
            <p className={className.liLabelClass}>
              {translate(intl, 'ui.PCP.PRODUCT_CARD.CONSUMPTION')}
            </p>
            <p className={className.liValueClass}>{consumptionValue}</p>
          </li>
          <li className={className.liWrapperClass}>
            <p className={className.liLabelClass}>
              {translate(intl, 'ui.PCP.PRODUCT_CARD.CO2_LABEL')}
            </p>
            <p className={className.liValueClass}>{emissionValue}</p>
          </li>
          <li className={className.liWrapperClass}>
            <p className={className.liLabelClass}>
              {translate(intl, 'ui.PCP.PRODUCT_CARD.CO2_CLASS')}
            </p>
            <p className={className.liValueClass}>
              {vehicle.co2Class ? vehicle.co2Class : '-'}
            </p>
          </li>
          {vehicle.fuelType === FuelType.HYBRID && (
            <li className={className.liWrapperClass}>
              <p className={className.liLabelClass}>
                {translate(
                  intl,
                  'ui.PCP.PRODUCT_CARD.CO2_CLASS_DISCHARGED_BATTERY_WLTP'
                )}
              </p>
              <p className={className.liValueClass}>
                {vehicle.co2ClassDischargedBatteryWltp
                  ? vehicle.co2ClassDischargedBatteryWltp
                  : '-'}
              </p>
            </li>
          )}
        </ul>
      </div>
      {vehicle.price && (
        <div className={className.priceClass}>
          <div>
            <Price
              isMonthlyMainPrice={selectedFinance.key !== PaymentType.CashRange}
              price={vehicle.price}
              monthlyPrice={monthlyPrice || undefined}
              displayView={view}
              priceTextKey="ui.PRICE.PRICE_ONLY"
              mwstPcp={
                vehiclePropExists(vehicle.mwst) &&
                vehicle.mwst &&
                vehicle.vehicleCondition === VehicleCondition.USED
              }
              financingDetailsLinkPcp={vehicle.financingDetails?.length > 0}
              financeTypeText={financeTypeText}
              refTextlink={refTextlink}
              handleFinancingDetailsClick={handleFinancingDetailsClick}
            />
          </div>
          <ArrowRight32 className={className.arrowClass} />
        </div>
      )}
      {loading && (
        <div className={className.loadingOverlay}>
          <CircularLoader type={CircularLoaderType.secondary} />
        </div>
      )}
    </article>
  );
};

export default ProductCard;
